// packages/core/src/sync/SyncErrors.ts
// ─────────────────────────────────────────────────────────────────────────
// Error taxonomy for the sync/network layer. Platform-agnostic (no DOM lib):
// this classifies failures thrown by HttpSyncClient into a small set of
// categories the VIEW layer can branch on, and pulls the server's traceId out
// of a ProblemDetails body so the UI can surface it for support.
//
// WHY A SEPARATE MODULE: HttpSyncClient throws two concrete types today
// (SyncHttpError, NotAuthenticatedError). Rather than make every caller
// instanceof-check both plus "is this a TypeError from fetch?", classify()
// collapses any thrown value into one discriminated union. Callers switch on
// `.category` and get a user-safe message + optional traceId for free.
//
// The API's GlobalExceptionHandler returns ProblemDetails with a top-level
// `traceId` extension on every 5xx (and many 4xx). extractTraceId parses it
// defensively from the SyncHttpError.bodyText — the body is already captured
// there, so no extra read.
// ─────────────────────────────────────────────────────────────────────────

import { SyncHttpError, NotAuthenticatedError } from './HttpSyncClient';

/**
 * Coarse failure classes the UI cares about. Deliberately small:
 *   • offline  — no network reached the server (fetch rejected). Retryable.
 *   • auth     — 401/403 or NotAuthenticatedError. Re-auth, don't retry blindly.
 *   • client   — 4xx the caller sent wrong. Show the server's title; don't retry.
 *   • server   — 5xx. Transient-ish; a bounded retry is reasonable. Has traceId.
 *   • unknown  — anything unclassifiable (a non-Error throw, a bug). Treat as
 *                a server-class failure for messaging but log loudly.
 */
export type SyncErrorCategory =
	| 'offline'
	| 'auth'
	| 'client'
	| 'server'
	| 'unknown';

/** Normalized, view-ready description of any failure from the sync layer. */
export interface ClassifiedSyncError {
	category: SyncErrorCategory;
	/** HTTP status when there was a response; 0 when the request never landed. */
	status: number;
	/**
	 * A message safe to show a user. Never the raw server body or a stack — a
	 * short, plain, actionable sentence per category. The interface's own voice.
	 */
	userMessage: string;
	/** Server correlation id when present (ProblemDetails.traceId). */
	traceId: string | null;
	/** Whether a bounded automatic retry is sensible for this failure. */
	retryable: boolean;
	/** The original thrown value, for logging/telemetry. Never shown to users. */
	cause: unknown;
}

/**
 * Pull `traceId` out of a JSON ProblemDetails body. Returns null for a
 * non-JSON body, missing field, or any parse error — this must never throw.
 */
export function extractTraceId(bodyText: string): string | null {
	if (!bodyText) return null;
	try {
		const parsed: unknown = JSON.parse(bodyText);
		if (parsed !== null && typeof parsed === 'object') {
			const id = (parsed as { traceId?: unknown }).traceId;
			if (typeof id === 'string' && id.length > 0) return id;
		}
	} catch {
		/* body wasn't JSON — no traceId to extract */
	}
	return null;
}

/**
 * Heuristic for "the request never reached the server" (as opposed to reaching
 * it and getting an error status). A rejected `fetch` throws a TypeError in
 * browsers ("Failed to fetch") and RN ("Network request failed"); we match on
 * both the type and the message so a genuine offline read is classed as
 * retryable 'offline' rather than 'unknown'.
 */
function looksLikeNetworkFailure(err: unknown): boolean {
	if (err instanceof TypeError) return true;
	if (err instanceof Error) {
		const m = err.message.toLowerCase();
		return (
			m.includes('failed to fetch') ||
			m.includes('network request failed') ||
			m.includes('networkerror') ||
			m.includes('load failed') // Safari's phrasing
		);
	}
	return false;
}

/**
 * Collapse any thrown value from the sync layer into a ClassifiedSyncError.
 * Order matters: check the typed errors first, then the network heuristic,
 * then fall through to 'unknown'.
 */
export function classifySyncError(err: unknown): ClassifiedSyncError {
	// Unauthenticated before any request went out (no token).
	if (err instanceof NotAuthenticatedError) {
		return {
			category: 'auth',
			status: 401,
			userMessage: 'Please sign in again to continue.',
			traceId: null,
			retryable: false,
			cause: err,
		};
	}

	// Reached the server and got a non-ok, non-allowed status.
	if (err instanceof SyncHttpError) {
		const traceId = extractTraceId(err.bodyText);

		if (err.status === 401 || err.status === 403) {
			return {
				category: 'auth',
				status: err.status,
				userMessage: 'Your session expired. Sign in again to continue.',
				traceId,
				retryable: false,
				cause: err,
			};
		}

		if (err.status >= 500) {
			return {
				category: 'server',
				status: err.status,
				userMessage:
					'Something went wrong on our end. Please try again in a moment.',
				traceId,
				retryable: true,
				cause: err,
			};
		}

		// Remaining 4xx: the request was wrong. Not retryable as-is.
		return {
			category: 'client',
			status: err.status,
			userMessage:
				'That request could not be completed. Please refresh and try again.',
			traceId,
			retryable: false,
			cause: err,
		};
	}

	// Never reached the server.
	if (looksLikeNetworkFailure(err)) {
		return {
			category: 'offline',
			status: 0,
			userMessage:
				"Can't reach the server. Check your connection and try again.",
			traceId: null,
			retryable: true,
			cause: err,
		};
	}

	// Genuinely unexpected — a bug or a non-Error throw. Message like a server
	// failure (users can't act on internals) but flag it for loud logging.
	return {
		category: 'unknown',
		status: 0,
		userMessage:
			'Something went wrong. Please try again, and let us know if it keeps happening.',
		traceId: null,
		retryable: false,
		cause: err,
	};
}
